import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Select,
  Switch,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { FeatureVisibility } from '@snap-types/models/enums';
import { SnapSiteConfigEditFormDataUnvalidated } from '@snap-types/models/snap-site-config';
import { FieldPath, get, useFormContext } from 'react-hook-form';

import { ControlGroupCard } from './components/ControlGroupCard';

export const PartInsightsPanel = () => {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();

  return (
    <Flex direction="column" gap={4}>
      <PartInsightsCard
        groupTitle="Part Insights"
        visibilityFieldName="partInsightsPartHistoryVisible"
      >
        <FormControl
          isInvalid={
            !!get(form.formState.errors, 'partInsightsMaxDaysForRecent')
          }
        >
          <FormLabel htmlFor="part_insights_max_days_for_recent_input">
            Max age to be <strong>Recent</strong>? (in days)
          </FormLabel>

          <Input
            id="part_insights_max_days_for_recent_input"
            type="number"
            inputMode="numeric"
            {...form.register('partInsightsMaxDaysForRecent')}
            min={0}
          />

          <ErrorMessage
            name="partInsightsMaxDaysForRecent"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
        </FormControl>

        <FormControl
          isInvalid={
            !!get(form.formState.errors, 'partInsightsMaxDaysForInclusion')
          }
        >
          <FormLabel htmlFor="part_insights_max_days_for_inclusion_input">
            Max age to be included <strong>at all</strong>? (in days)
          </FormLabel>

          <Input
            id="part_insights_max_days_for_inclusion_input"
            type="number"
            inputMode="numeric"
            {...form.register('partInsightsMaxDaysForInclusion')}
            min={0}
          />

          <ErrorMessage
            name="partInsightsMaxDaysForInclusion"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
        </FormControl>

        <FormControl
          isInvalid={!!get(form.formState.errors, 'partInsightsLowWinRate')}
        >
          <FormLabel htmlFor="part_insights_low_win_rate_input">
            Low Win Rate Threshold
          </FormLabel>

          <Input
            id="part_insights_low_win_rate_input"
            type="number"
            inputMode="numeric"
            {...form.register('partInsightsLowWinRate', {
              deps: ['partInsightsHighWinRate'],
            })}
            min={0}
            max={0.99}
            step="0.01"
          />

          <ErrorMessage
            name="partInsightsLowWinRate"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
          <FormHelperText>
            As a decimal percent between <strong>0.00</strong> and{' '}
            <strong>0.99</strong>
          </FormHelperText>
        </FormControl>

        <FormControl
          isInvalid={!!get(form.formState.errors, 'partInsightsHighWinRate')}
        >
          <FormLabel htmlFor="part_insights_high_win_rate_input">
            High Win Rate Threshold
          </FormLabel>

          <Input
            id="part_insights_high_win_rate_input"
            type="number"
            inputMode="numeric"
            {...form.register('partInsightsHighWinRate', {
              deps: ['partInsightsLowWinRate'],
            })}
            min={0}
            max={0.99}
            step="0.01"
          />

          <ErrorMessage
            name="partInsightsHighWinRate"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
          <FormHelperText>
            As a decimal percent between <strong>0.00</strong> and{' '}
            <strong>0.99</strong>
          </FormHelperText>
        </FormControl>
      </PartInsightsCard>

      <PartInsightsCard
        groupTitle="Lead Time Insights"
        visibilityFieldName="partInsightsLeadTimeVisible"
      >
        <FormControl
          isInvalid={
            !!get(form.formState.errors, 'partInsightsLeadTimeRangeLow')
          }
        >
          <FormLabel htmlFor="part_insights_lead_time_range_low">
            'Slightly' is More Than . . .
          </FormLabel>

          <Input
            id="part_insights_lead_time_range_low"
            type="number"
            inputMode="numeric"
            {...form.register('partInsightsLeadTimeRangeLow', {
              deps: ['partInsightsLeadTimeRangeHigh'],
            })}
            min={0}
            max={9.99}
            step="0.01"
          />

          <ErrorMessage
            name="partInsightsLeadTimeRangeLow"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />

          <FormHelperText>
            As a decimal percent between <strong>0.00</strong> and{' '}
            <strong>9.99</strong> (e.g. 3 = 300%)
          </FormHelperText>
        </FormControl>

        <FormControl
          isInvalid={
            !!get(form.formState.errors, 'partInsightsLeadTimeRangeHigh')
          }
        >
          <FormLabel htmlFor="part_insights_lead_time_range_high">
            'Significantly' is More Than . . .
          </FormLabel>

          <Input
            id="part_insights_lead_time_range_high"
            type="number"
            inputMode="numeric"
            {...form.register('partInsightsLeadTimeRangeHigh', {
              deps: ['partInsightsLeadTimeRangeLow'],
            })}
            min={0}
            max={9.99}
            step="0.01"
          />

          <ErrorMessage
            name="partInsightsLeadTimeRangeHigh"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />

          <FormHelperText>
            As a decimal percent between <strong>0.00</strong> and{' '}
            <strong>9.99</strong> (e.g. 3 = 300%)
          </FormHelperText>
        </FormControl>

        <FormControl
          isInvalid={
            !!get(
              form.formState.errors,
              'partInsightsLeadTimeMaxDaysForInclusion',
            )
          }
        >
          <FormLabel htmlFor="part_insights_lead_time_max_days_for_inclusion">
            Max Order Age to include in Lead Time Average (in days)
          </FormLabel>

          <Input
            id="part_insights_lead_time_max_days_for_inclusion"
            type="number"
            inputMode="numeric"
            {...form.register('partInsightsLeadTimeMaxDaysForInclusion')}
            min={0}
          />

          <ErrorMessage
            name="partInsightsLeadTimeMaxDaysForInclusion"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
        </FormControl>
      </PartInsightsCard>
      <PartInsightsCard
        groupTitle="Most Recent Order Gross Margin"
        visibilityFieldName="partInsightsLastOrderGrossMarginVisible"
      >
        <FormControl
          isInvalid={
            !!get(
              form.formState.errors,
              'partInsightsLastOrderGrossMarginThreshold',
            )
          }
        >
          <FormLabel htmlFor="part_insights_last_order_gross_margin_threshold_input">
            Gross Margin Threshold
          </FormLabel>

          <Input
            id="part_insights_last_order_gross_margin_threshold_input"
            type="number"
            inputMode="numeric"
            {...form.register('partInsightsLastOrderGrossMarginThreshold')}
            max={1}
            step="0.01"
          />

          <ErrorMessage
            name="partInsightsLastOrderGrossMarginThreshold"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
          <FormHelperText>As a decimal percent less than 1.00</FormHelperText>
        </FormControl>
      </PartInsightsCard>

      <PartInsightsCard
        groupTitle="Cross-Site Part/Customer Part IDs"
        visibilityFieldName="partInsightsCrossSitePartIdVisible"
      />
      <ControlGroupCard
        title="Unsupported Calculations"
        dataGroupScope="part-insights"
      >
        <Grid
          gridTemplateColumns={{
            base: '1fr',
            md: '1fr 1fr',
            lg: '1fr 1fr 1fr',
          }}
          gap={4}
          width="auto"
        >
          <FormControl>
            <Flex gap={3} flexDirection="column">
              <FormLabel htmlFor={`enableUnsupportedCalculationsFlag`}>
                Enable Unsupported Costs Warning
              </FormLabel>
              <Switch
                {...form.register(`enableUnsupportedCalculationsFlag`)}
                aria-label="Enable unsupported costs warning"
              />
            </Flex>
          </FormControl>
        </Grid>
      </ControlGroupCard>
    </Flex>
  );
};

function PartInsightsCard({
  groupTitle,
  visibilityFieldName,
  children,
}: {
  groupTitle: string;
  visibilityFieldName: FieldPath<SnapSiteConfigEditFormDataUnvalidated>;
  children?: React.ReactNode;
}) {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();

  return (
    <ControlGroupCard
      title={groupTitle}
      dataGroupScope={`part-insights-${groupTitle}`}
    >
      <Grid
        gridTemplateColumns={{
          base: '1fr',
          md: '1fr 1fr',
          lg: '1fr 1fr 1fr',
        }}
        gap={4}
        width="auto"
      >
        <FormControl>
          <FormLabel htmlFor={`${visibilityFieldName}_input`}>
            {groupTitle} Visibility
          </FormLabel>

          <Select
            {...form.register(visibilityFieldName)}
            id={`${visibilityFieldName}_input`}
          >
            <option value={FeatureVisibility.NONE}>No One</option>
            <option value={FeatureVisibility.SUPER_ADMIN}>
              Super Admins Only
            </option>
            <option value={FeatureVisibility.ALL}>All Users</option>
          </Select>
        </FormControl>
        {children}
      </Grid>
    </ControlGroupCard>
  );
}
